// eslint-disable-next-line no-unused-vars
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import OfferEditor from 'Containers/Offers/OfferEditor';
import useAsyncError from 'Hooks/useAsyncError';
import useCart from 'Hooks/useCart';
import useExchangeRates from 'Hooks/useExchangeRates';
import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import useToastNotifications from 'Hooks/useToastNotifications';
import { API_OFFERS } from 'Constants';
import download from 'Utils/download';
import isOfferExpired, {
  clampMarkupPercent,
  getOfferProductErrors,
  getOfferValidUntil,
  isProductUnavailableForOffer,
  isValidClientEmail,
  OFFER_PRODUCT_STATUS_TRANSLATIONS
} from 'Utils/offers';

const getProductId = product => product?.productId || product?.id;

const getSavedOffer = response => {
  const offer = response?.offer
  || response?.data?.offer
  || response?.data
  || response;

  return offer ? {
    ...offer,
    currencyId: String(offer.currencyId || 'ron').toLowerCase()
  } : offer;
};

const getItemPayload = product => ({
  productId: String(getProductId(product)),
  quantity: Number(product.quantity || product.minQuantity || 1),
  ...(product.supplierId && { supplierId: product.supplierId }),
  markupPercent: product.markupPercent === null || product.markupPercent === undefined
    ? null
    : clampMarkupPercent(product.markupPercent),
  unitCustomPrice: product.unitCustomPrice ?? null
});

const getOfferPayload = offer => ({
  name: offer.name,
  clientName: offer.clientName,
  clientEmail: offer.clientEmail,
  currencyId: String(offer.currencyId || 'ron').toLowerCase(),
  items: offer.items.map(getItemPayload),
  defaultMarkupPercent: clampMarkupPercent(offer.defaultMarkupPercent),
  validUntil: offer.validUntil || null
});

const getPatchPayload = (dirtyFields, offer) => {
  const payload = {};
  let error = null;

  if (dirtyFields.has('name')) {
    const name = String(offer.name || '').trim();
    if (name.length >= 3 && name.length <= 255) {
      payload.name = name;
    } else {
      error = error || 'offers.offerNameLength';
    }
  }
  if (dirtyFields.has('clientName')) {
    const clientName = String(offer.clientName || '').trim();
    if (clientName.length <= 255) {
      payload.clientName = clientName;
    } else {
      error = error || 'offers.requiredClientName';
    }
  }
  if (dirtyFields.has('clientEmail')) {
    const clientEmail = String(offer.clientEmail || '').trim();
    if ((!clientEmail || isValidClientEmail(clientEmail)) && clientEmail.length <= 255) {
      payload.clientEmail = clientEmail;
    } else {
      error = error || 'offers.invalidClientEmail';
    }
  }
  if (dirtyFields.has('currencyId') && offer.currencyId) {
    payload.currencyId = String(offer.currencyId).toLowerCase();
  }
  if (dirtyFields.has('defaultMarkupPercent')) {
    payload.defaultMarkupPercent = clampMarkupPercent(offer.defaultMarkupPercent);
  }
  if (dirtyFields.has('validUntil')) {
    payload.validUntil = offer.validUntil;
  }
  if (dirtyFields.has('items')) {
    if (offer.items.length < 1 || offer.items.length > 500) {
      error = error || 'offers.invalidItemCount';
    } else if (offer.items.some(
      item => !Number.isInteger(Number(item.quantity)) || Number(item.quantity) <= 0
    )) {
      error = error || 'offers.invalidItemQuantity';
    } else {
      payload.items = offer.items.map(getItemPayload);
    }
  }

  return { payload, error };
};

const mergeOfferItems = (serverOffer, localItems) => ({
  ...serverOffer,
  items: (serverOffer.items || localItems).map(serverItem => {
    const localItem = localItems.find(
      item => String(getProductId(item)) === String(getProductId(serverItem))
    );
    return {
      ...localItem,
      ...serverItem
    };
  })
});

const SavedOfferEditor = memo(() => {
  const { t } = useTranslation();
  const fetcher = useFetch();
  const { refresh: refreshCart } = useCart();
  const { exchangeRates, exchangeRatesLoading } = useExchangeRates();
  const throwError = useAsyncError();
  const toast = useToastNotifications();
  const { params } = useRouter();
  const [offer, setOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [productErrors, setProductErrors] = useState([]);
  const offerRef = useRef(null);
  const dirtyFieldsRef = useRef(new Set());

  const setCurrentOffer = useCallback(nextOffer => {
    offerRef.current = nextOffer;
    setOffer(nextOffer);
  }, []);

  const sendPatch = useCallback(async offerId => {
    const dirtyFields = dirtyFieldsRef.current;
    dirtyFieldsRef.current = new Set();
    if (!dirtyFields.size || !offerRef.current) return true;

    const { payload, error: validationError } = getPatchPayload(dirtyFields, offerRef.current);
    if (validationError) {
      toast.addNotification({ type: 'error', title: t(validationError) });
    }
    if (!Object.keys(payload).length) {
      return !validationError;
    }

    setIsSaving(true);
    try {
      const response = await fetcher(`${ API_OFFERS }/${ encodeURIComponent(offerId) }`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      if (payload.currencyId) {
        let savedOffer = getSavedOffer(response);
        if (!savedOffer?.items) {
          savedOffer = getSavedOffer(await fetcher(
            `${ API_OFFERS }/${ encodeURIComponent(offerId) }`
          ));
        }
        const currentOffer = offerRef.current;
        if (
          savedOffer?.items
          && currentOffer
          && String(currentOffer.id) === String(offerId)
        ) {
          setCurrentOffer(mergeOfferItems(savedOffer, currentOffer.items));
        }
      }
      if (payload.items) setProductErrors([]);
      return !validationError;
    } catch (error) {
      const nextProductErrors = getOfferProductErrors(error);
      if (nextProductErrors.length) setProductErrors(nextProductErrors);
      // Preserve the pending changes so the next save retries them instead of dropping the edit.
      dirtyFields.forEach(field => dirtyFieldsRef.current.add(field));
      toast.addNotification({
        type: 'error',
        title: nextProductErrors.length
          ? t(
            OFFER_PRODUCT_STATUS_TRANSLATIONS[nextProductErrors[0].status]
            || 'offers.cartStatusProductUnavailable'
          )
          : error?.info?.errors?.[0]?.message
          || error?.message
          || t('offers.saveFailed')
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [fetcher, setCurrentOffer, t, toast]);

  const debouncedPatch = useMemo(
    () => debounce(sendPatch, 700),
    [sendPatch]
  );

  const queuePatch = useCallback((offerId, changes) => {
    Object.keys(changes).forEach(field => dirtyFieldsRef.current.add(field));
    debouncedPatch(offerId);
  }, [debouncedPatch]);

  const flushPatch = useCallback(async () => {
    debouncedPatch.cancel();
    return sendPatch(offerRef.current?.id);
  }, [debouncedPatch, sendPatch]);

  useEffect(() => () => {
    debouncedPatch.flush();
  }, [debouncedPatch]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setOffer(null);
    setProductErrors([]);

    fetcher(`${ API_OFFERS }/${ encodeURIComponent(params.id) }`)
      .then(response => {
        if (isActive) {
          const savedOffer = getSavedOffer(response);
          setCurrentOffer(savedOffer);
          setProductErrors((savedOffer?.items || [])
            .filter(item => item?.productId && item?.status)
            .map(item => ({
              productId: item.productId,
              supplierId: item.supplierId,
              status: item.status
            })));
        }
      })
      .catch(error => {
        if (isActive && error?.code !== 20) throwError(error);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [fetcher, params.id, setCurrentOffer, throwError]);

  const updateOffer = useCallback((offerId, changes) => {
    const currentOffer = offerRef.current;
    if (!currentOffer || String(currentOffer.id) !== String(offerId)) return;
    const nextChanges = typeof changes === 'function' ? changes(currentOffer) : changes;
    const nextOffer = {
      ...currentOffer,
      ...nextChanges
    };
    setCurrentOffer(nextOffer);
    queuePatch(offerId, nextChanges);
  }, [queuePatch, setCurrentOffer]);

  const updateOfferProduct = useCallback((offerId, productId, changes) => {
    updateOffer(offerId, currentOffer => ({
      items: currentOffer.items.map(product => (
        String(getProductId(product)) === String(productId)
          ? { ...product, ...changes }
          : product
      ))
    }));
  }, [updateOffer]);

  const removeProductFromOffer = useCallback((offerId, productId) => {
    updateOffer(offerId, currentOffer => ({
      items: currentOffer.items.filter(
        product => String(getProductId(product)) !== String(productId)
      )
    }));
  }, [updateOffer]);

  const handleRefreshPrices = useCallback(async offerId => {
    const flushed = await flushPatch();
    if (!flushed) return null;
    const response = await fetcher(
      `${ API_OFFERS }/${ encodeURIComponent(offerId) }/reprice`,
      { method: 'POST' }
    );
    let repricedOffer = getSavedOffer(response);

    if (!repricedOffer?.items) {
      const refreshedResponse = await fetcher(
        `${ API_OFFERS }/${ encodeURIComponent(offerId) }`
      );
      repricedOffer = getSavedOffer(refreshedResponse);
    }

    setCurrentOffer(repricedOffer);
    return repricedOffer;
  }, [fetcher, flushPatch, setCurrentOffer]);

  const handleRenew = useCallback(async offerId => {
    const currentOffer = offerRef.current;
    if (!currentOffer || String(currentOffer.id) !== String(offerId)) return null;

    const flushed = await flushPatch();
    if (!flushed) return null;

    const renewedOffer = {
      ...currentOffer,
      validUntil: getOfferValidUntil()
    };
    const response = await fetcher(
      `${ API_OFFERS }/${ encodeURIComponent(offerId) }`,
      {
        method: 'PATCH',
        body: JSON.stringify({ validUntil: renewedOffer.validUntil })
      }
    );
    const responseOffer = getSavedOffer(response);
    const savedOffer = responseOffer?.id
      ? mergeOfferItems(responseOffer, renewedOffer.items)
      : renewedOffer;

    setCurrentOffer(savedOffer);
    return savedOffer;
  }, [fetcher, flushPatch, setCurrentOffer]);

  const handleExport = useCallback(async ({ offerId, format }) => {
    const flushed = await flushPatch();
    if (!flushed) {
      // Abort the download; flushPatch already surfaced the save error.
      throw new Error();
    }
    return download(
      `${ API_OFFERS }/${ encodeURIComponent(offerId) }/export/${ encodeURIComponent(format) }`
    );
  }, [flushPatch]);

  const handleAddToCartPreview = useCallback(async offerToPreview => {
    const flushed = await flushPatch();
    if (!flushed) return null;
    return fetcher(
      `${ API_OFFERS }/${ encodeURIComponent(offerToPreview.id) }/add-to-cart/preview`
    );
  }, [fetcher, flushPatch]);

  const handleAddOfferToCart = useCallback(async preview => {
    const response = await fetcher(
      `${ API_OFFERS }/${ encodeURIComponent(preview.offerId) }/add-to-cart?only_offerable_products=1`,
      { method: 'POST' }
    );
    await refreshCart();
    return response;
  }, [fetcher, refreshCart]);

  const addProductToOffer = useCallback(async (offerId, product) => {
    const currentOffer = offerRef.current;
    if (!currentOffer || String(currentOffer.id) !== String(offerId)) return null;
    if (isOfferExpired(currentOffer)) return { offer: currentOffer, expired: true };
    if (isProductUnavailableForOffer(product)) {
      return { offer: currentOffer, unavailable: true };
    }

    const minQuantity = Math.max(1, Number(product.minQuantity || 1));

    const productId = getProductId(product);
    const alreadyExists = currentOffer.items.some(
      item => String(getProductId(item)) === String(productId)
    );
    if (alreadyExists) return { offer: currentOffer, alreadyExists: true };

    const updatedOffer = {
      ...currentOffer,
      items: currentOffer.items.concat({
        ...product,
        quantity: product.quantity || minQuantity,
        markupPercent: product.markupPercent ?? currentOffer.defaultMarkupPercent ?? 0,
        unitCustomPrice: product.unitCustomPrice ?? null
      })
    };
    const flushed = await flushPatch();
    if (!flushed) {
      // Don't persist on top of unsaved edits that failed to reach the server.
      return { offer: currentOffer, saveFailed: true };
    }
    const response = await fetcher(
      `${ API_OFFERS }/${ encodeURIComponent(offerId) }`,
      {
        method: 'PUT',
        body: JSON.stringify(getOfferPayload(updatedOffer))
      }
    );
    let savedOffer = getSavedOffer(response);

    if (!savedOffer?.items) {
      const refreshedResponse = await fetcher(
        `${ API_OFFERS }/${ encodeURIComponent(offerId) }`
      );
      savedOffer = getSavedOffer(refreshedResponse);
    }

    const mergedOffer = mergeOfferItems(savedOffer, updatedOffer.items);
    setCurrentOffer(mergedOffer);
    return { offer: mergedOffer, alreadyExists: false };
  }, [fetcher, flushPatch, setCurrentOffer]);

  return (
    <OfferEditor
      offer={ offer }
      exchangeRates={ exchangeRates }
      isLoading={ isLoading || exchangeRatesLoading }
      isSaving={ isSaving }
      isSavedOffer
      productErrors={ productErrors }
      updateOffer={ updateOffer }
      addProductToOffer={ addProductToOffer }
      updateOfferProduct={ updateOfferProduct }
      removeProductFromOffer={ removeProductFromOffer }
      onRefreshPrices={ handleRefreshPrices }
      onRenew={ handleRenew }
      exportOffer={ handleExport }
      previewAddToCart={ handleAddToCartPreview }
      addOfferToCart={ handleAddOfferToCart }
    />
  );
});

SavedOfferEditor.displayName = 'SavedOfferEditorContainer';

export default SavedOfferEditor;
